import { useState } from 'react'
import { useFigmaStore } from './useFigmaStore'
import { usePulseMemory } from '../PulseMemory/usePulseMemory'
import { authFetch } from '../../lib/api'

export function FigmaViewer() {
  const project = usePulseMemory(s => s.memory.project) || 'default'
  const { pages, figmaUrl, token, selectedNode, setSelectedNode, code, setCode, appendCode, loading, setLoading, error, setError, reset } = useFigmaStore()
  const [fileName, setFileName] = useState('src/components/FigmaDesign.tsx')
  const [saved, setSaved] = useState(false)
  const [savedPath, setSavedPath] = useState('')

  const handleImport = async (nodeId: string) => {
    if (!figmaUrl || !token) return
    setSelectedNode(nodeId)
    setLoading(true); setCode(''); setError(''); setSaved(false)
    try {
      const r = await authFetch('/api/figma/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ figmaUrl, token, nodeId }),
      })
      if (!r.ok) { setError((await r.json()).error); setLoading(false); return }

      const reader = r.body!.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'text') appendCode(data.text)
            else if (data.type === 'error') setError(data.error)
          } catch {}
        }
      }
    } catch (e: any) { setError(e.message) }
    setLoading(false)
  }

  const handleSave = async () => {
    if (!code || !fileName) return
    let cleanCode = code
    const fenceMatch = cleanCode.match(/```(?:tsx|jsx|typescript)?\n([\s\S]*?)```/)
    if (fenceMatch) cleanCode = fenceMatch[1]

    const r = await authFetch('/api/figma/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project, filePath: fileName, code: cleanCode }),
    })
    if (r.ok) {
      setSaved(true)
      setSavedPath(`${project}/${fileName}`)
    }
    else setError((await r.json()).error)
  }

  const btn: React.CSSProperties = { padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }
  const btnSec: React.CSSProperties = { ...btn, background: 'var(--bg-3)', border: '1px solid var(--border)', color: 'var(--text-1)' }
  const input: React.CSSProperties = { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }

  // No pages loaded yet
  if (pages.length === 0) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎨</div>
          <div>Enter Figma URL + token in the sidebar and click "Load Pages"</div>
        </div>
      </div>
    )
  }

  // Show generated code
  if (code) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{project}/</span>
          <input style={{ ...input, flex: 1, minWidth: 200 }} value={fileName} onChange={e => setFileName(e.target.value)} />
          <button style={{ ...btn, background: '#5fd4a4' }} onClick={handleSave} disabled={saved}>
            {saved ? '✅ Saved!' : '💾 Create File'}
          </button>
          <button style={btnSec} onClick={() => navigator.clipboard.writeText(code)}>📋 Copy</button>
          <button style={btnSec} onClick={() => { setCode(''); setSaved(false); setSavedPath('') }}>← Back to pages</button>
        </div>
        {saved && savedPath && (
          <div style={{ padding: '8px 16px', background: 'rgba(95,212,164,0.1)', borderBottom: '1px solid var(--border)', fontSize: 11, fontFamily: 'var(--font-mono)', color: '#5fd4a4' }}>
            ✅ Saved to <strong>{savedPath}</strong> — Tell the agent: "Use the component from {fileName}"
          </div>
        )}
        <pre style={{ flex: 1, margin: 0, padding: 16, overflow: 'auto', fontSize: 11, fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', color: 'var(--text-2)', background: 'var(--bg-1)' }}>{code}</pre>
      </div>
    )
  }

  // Show page thumbnails
  return (
    <div style={{ width: '100%', height: '100%', overflow: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>
          Click a page to generate code ({pages.length} pages)
        </span>
        <button style={btnSec} onClick={reset}>↺ Reset</button>
      </div>

      {error && <div style={{ marginBottom: 12, padding: 10, background: '#dc262620', border: '1px solid #dc2626', borderRadius: 6, color: '#f87171', fontSize: 11, fontFamily: 'var(--font-mono)' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {pages.map(page => (
          <button
            key={page.id}
            onClick={() => handleImport(page.id)}
            disabled={loading}
            style={{ border: selectedNode === page.id ? '2px solid var(--accent)' : '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-2)', cursor: loading ? 'wait' : 'pointer', padding: 0, textAlign: 'center', transition: 'transform 0.1s, box-shadow 0.1s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.02)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none' }}
          >
            <div style={{ width: '100%', height: 200, background: 'var(--bg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {page.imageUrl ? (
                <img src={page.imageUrl} alt={page.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
              ) : (
                <span style={{ fontSize: 40, opacity: 0.3 }}>📄</span>
              )}
            </div>
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
              {loading && selectedNode === page.id ? '⏳ Generating...' : page.name}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
